/**
 * MobileBottomBar — sticky `[Save] [primary CTA]` bar visible only on
 * mobile (md:hidden) on the property-file page.
 *
 * Per redesign decision v1.2 item 6: save-to-account is contextual. On
 * mobile we surface the primary CTA + Save in a sticky bottom bar after
 * the user has shown engagement (scrolled past the data tree). Until
 * then the bar stays hidden so the page lands clean.
 *
 * Implementation: a passive scroll listener gated through a useEffect
 * so the component is SSR-safe (we're a Vite SPA, but defensive).
 * Threshold = 240px scrollY, which roughly maps to "user has read past
 * the address + provenance line + first preview row" on a 375px viewport.
 *
 * Sign-in plumbing: `onSave` lets the parent decide whether to push the
 * user to /login with `state.returnTo` set to the signed-in property
 * route. We don't hard-code the return path here so the same component
 * works for both anonymous and signed-in surfaces if reused.
 */

import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

interface MobileBottomBarBaseProps {
  /** Visible label for the primary CTA. */
  primaryLabel: string;
  /** Optional click handler for the Save button. */
  onSave?: () => void;
  /** Override the engagement threshold (px). Defaults to 240. */
  scrollThresholdPx?: number;
  /** Force the bar visible (useful for tests/storybook). */
  forceVisible?: boolean;
  /** When true, disables the primary action (e.g. Stripe redirect in flight). */
  isProcessing?: boolean;
}

interface MobileBottomBarLinkProps extends MobileBottomBarBaseProps {
  /** href the primary CTA navigates to. */
  primaryHref: string;
  onPrimaryAction?: never;
}

interface MobileBottomBarButtonProps extends MobileBottomBarBaseProps {
  primaryHref?: never;
  /** Click handler for the primary CTA. */
  onPrimaryAction: () => void | Promise<void>;
}

type MobileBottomBarProps = MobileBottomBarLinkProps | MobileBottomBarButtonProps;

export default function MobileBottomBar(props: MobileBottomBarProps) {
  const {
    primaryLabel,
    onSave,
    scrollThresholdPx = 240,
    forceVisible = false,
    isProcessing = false,
  } = props;
  const [visible, setVisible] = useState<boolean>(forceVisible);

  useEffect(() => {
    if (forceVisible) {
      setVisible(true);
      return;
    }
    if (typeof window === 'undefined') return; // SSR-safe guard

    const handleScroll = () => {
      setVisible(window.scrollY > scrollThresholdPx);
    };

    // Run once on mount in case the user lands mid-scroll (e.g. fragment).
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollThresholdPx, forceVisible]);

  if (!visible) return null;

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.05)]"
      data-testid="mobile-bottom-bar"
      role="region"
      aria-label="Property actions"
    >
      <div className="flex items-center gap-2">
        {onSave && (
          <button
            type="button"
            onClick={onSave}
            className="flex-1 inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-950 shadow-sm hover:bg-slate-50 min-h-[44px]"
            data-testid="mobile-bar-save"
          >
            Save
          </button>
        )}
        {'onPrimaryAction' in props && props.onPrimaryAction ? (
          <button
            type="button"
            onClick={() => void props.onPrimaryAction()}
            disabled={isProcessing}
            aria-busy={isProcessing || undefined}
            className="flex-[2] inline-flex items-center justify-center rounded-lg bg-emerald-700 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 active:bg-emerald-900 disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
            data-testid="mobile-bar-primary"
          >
            {primaryLabel}
          </button>
        ) : (
          <Link
            to={(props as MobileBottomBarLinkProps).primaryHref}
            className="flex-[2] inline-flex items-center justify-center rounded-lg bg-emerald-700 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 active:bg-emerald-900 no-underline min-h-[44px]"
            data-testid="mobile-bar-primary"
          >
            {primaryLabel}
          </Link>
        )}
      </div>
    </div>
  );
}
